import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  SafTable,
  SafIcon,
  SafButton,
  SafAlert,
  SafDialog,
  SafText,
} from "@saffron/core-components/react";
import SafTextComp from "./Saftext";
import { useParams, useNavigate } from "react-router-dom";

// Utility function to get endpoint URL with operation parameter
const getEndpointUrl = (operation) => {
  const endpoint = `${process.env.REACT_APP_MIDDLEWARE_ENDPOINT}&operation=${operation}`;
  return endpoint;
};

const Actors = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    actor_name: "",
    location: "",
    description: "",
  });
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);
  const [actorData, setActorData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // States for edit functionality
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  // New states for delete functionality
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const buttonRef = useRef();
  const dialogRef = useRef();
  const deleteDialogRef = useRef(); // New ref for delete dialog
  const navigate = useNavigate();

  // const summaryInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
  // Get the asset ID from URL params
  const { assetId: urlAssetId } = useParams();
  const assetId = urlAssetId || "";

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (dialog) {
      dialog.addEventListener("hide", closeDialog);

      return () => {
        dialog.removeEventListener("hide", closeDialog);
      };
    }
  }, []);

  // New useEffect for delete dialog
  useEffect(() => {
    const deleteDialog = deleteDialogRef?.current;
    if (deleteDialog) {
      deleteDialog.addEventListener("hide", closeDeleteDialog);

      return () => {
        deleteDialog.removeEventListener("hide", closeDeleteDialog);
      };
    }
  }, []);

  const closeDialog = () => {
    setIsOpen(false);
    // Reset edit mode when dialog closes
    setIsEditMode(false);
    setEditingIndex(null);
    setFormData({
      actor_name: "",
      location: "",
      description: "",
    });
    setErrors({});
  };

  // New function to close delete dialog
  const closeDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleCancelClick = () => {
    dialogRef?.current?.hide();
  };

  // New function to handle delete dialog cancel
  const handleDeleteCancelClick = () => {
    deleteDialogRef?.current?.hide();
  };

  const handleOnClose = () => {
    setOpenAlertOptions(undefined);
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (formData.actor_name === "") {
      newErrors.actor_name = "Please enter the actor name";
    }

    if (formData.location === "") {
      newErrors.location = "Please enter the location";
    }

    if (formData.description === "") {
      newErrors.description = "Please enter the description";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to handle edit button click
  const handleEditClick = (actor, index) => {
    setIsEditMode(true);
    setEditingIndex(index);
    setFormData({
      actor_name: actor.actor_name,
      location: actor.location,
      description: actor.description,
    });
    setIsOpen(true);
  };

  // Function to handle add button click
  const handleAddClick = () => {
    setIsEditMode(false);
    setEditingIndex(null);
    setFormData({
      actor_name: "",
      location: "",
      description: "",
    });
    setIsOpen(true);
  };

  // New function to handle delete button click
  const handleDeleteClick = (actor, index) => {
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  // New function to handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteIndex === null) return;

    try {
      setIsLoading(true);

      const operation = "get";
      const getEndpoint = getEndpointUrl(operation);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Failed to check existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let responseData = await getResponse.json();
      console.log("Existing data response:", responseData);

      let documentData;

      // Handle if the response is an array (common with query results)
      if (Array.isArray(responseData) && responseData.length > 0) {
        documentData = responseData[0];
      }
      // Handle if the response is a single object
      else if (responseData && typeof responseData === "object") {
        documentData = responseData;
      } else {
        throw new Error("No existing document found to delete from");
      }

      // Initialize actors array if it doesn't exist
      if (!documentData.actors) {
        documentData.actors = [];
      }

      // Remove the actor at the specified index
      documentData.actors.splice(deleteIndex, 1);

      console.log("Document after deletion:", documentData);

      // Now update the document
      const insertOperation = "insert";
      const insertEndpoint = getEndpointUrl(insertOperation);

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: documentData,
          partitionKey: assetId,
        }),
      });

      if (!insertResponse.ok) {
        const errorText = await insertResponse.text();
        throw new Error(
          `Delete failed with status ${insertResponse.status}: ${errorText}`
        );
      }

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage("Actor was successfully deleted");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);

      // Close delete dialog
      deleteDialogRef?.current?.hide();

      // Refresh data
      await getActorsData();
    } catch (err) {
      console.error(err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage("Failed to delete actor: " + err.message);
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const getActorsData = useCallback(async () => {
    setIsLoading(true);

    if (!assetId) {
      console.error("No assetId found");
      setIsLoading(false);
      return;
    }

    const operation = "get";
    const endpoint = getEndpointUrl(operation);

    const dbName = process.env.REACT_APP_DB_TARGET;
    const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
    const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!response.ok) {
        console.error(`Error: ${response.status} ${response.statusText}`);
        setActorData(null);
        return;
      }

      const data = await response.json();
      console.log("Full response data:", data);

      // Handle if data is an array (common with query results)
      if (Array.isArray(data) && data.length > 0) {
        console.log("Setting actor data from array:", data[0]);
        setActorData(data[0]);
      }
      // Handle if data is a single object
      else if (data && typeof data === "object") {
        console.log("Setting actor data from object:", data);
        setActorData(data);
      } else {
        console.log("No valid actor data found in response");
        setActorData(null);
      }
    } catch (error) {
      console.error("Error fetching actors data:", error);
      setActorData(null);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  // useEffect(() => {
  //   getActorsData();
  // }, [getActorsData]);

  // Then modify the useEffect to match Integrations:
  useEffect(() => {
    if (urlAssetId) {
      // If we have a URL param, use it to fetch data
      getActorsData();
    } else if (localStorage.getItem("assetInfo")) {
      // If no URL param but we have localStorage data, redirect
      const storedInfo = JSON.parse(localStorage.getItem("assetInfo") || "{}");
      if (storedInfo.id) {
        navigate(`/${storedInfo.id}/actors`, { replace: true });
      } else {
        // No valid data, go to home
        navigate("/home");
      }
    } else {
      // No data at all, go to home
      navigate("/home");
    }
  }, [urlAssetId, navigate, getActorsData]);

  const handleSubmitForm = async (e) => {
    // Prevent default form submission behavior
    e.preventDefault();

    if (!validateForm()) {
      setOpenAlertOptions(["toast", "warning"]);
      setAlertMessage("Please fill the required fields");
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
      return;
    }

    const actorsInfo = {
      actor_name: formData.actor_name,
      location: formData.location,
      description: formData.description,
    };

    try {
      setIsLoading(true);

      const operation = "get";
      const getEndpoint = getEndpointUrl(operation);

      const dbName = process.env.REACT_APP_DB_TARGET;
      const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;
      const query = `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`;

      const getResponse = await fetch(getEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Failed to check existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let responseData = await getResponse.json();
      console.log("Existing data response:", responseData);

      let documentData;

      // Handle if the response is an array (common with query results)
      if (Array.isArray(responseData) && responseData.length > 0) {
        documentData = responseData[0];
      }
      // Handle if the response is a single object
      else if (responseData && typeof responseData === "object") {
        documentData = responseData;
      }
      // If no existing document found, create a new one
      else {
        documentData = {
          id: "architecture-details",
          asset_id: assetId,
          arch_diags: [],
          adrs: [],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Initialize actors array if it doesn't exist
      if (!documentData.actors) {
        documentData.actors = [];
      }

      // Handle edit vs add logic
      if (isEditMode && editingIndex !== null) {
        // Update existing actor
        documentData.actors[editingIndex] = actorsInfo;
        console.log("Updated actor at index:", editingIndex);
      } else {
        // Add new actor
        documentData.actors.push(actorsInfo);
        console.log("Added new actor");
      }

      console.log("Document to be saved:", documentData);

      // Now insert/update the document
      const insertOperation = "insert";
      const insertEndpoint = getEndpointUrl(insertOperation);

      const insertResponse = await fetch(insertEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: documentData,
          partitionKey: assetId,
        }),
      });

      if (!insertResponse.ok) {
        const errorText = await insertResponse.text();
        throw new Error(
          `Insert failed with status ${insertResponse.status}: ${errorText}`
        );
      }

      setOpenAlertOptions(["toast", "success"]);
      setAlertMessage(
        isEditMode
          ? "Actor was successfully updated"
          : "Actor was successfully added"
      );
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);

      // Reset form
      setFormData({
        actor_name: "",
        location: "",
        description: "",
      });
      setErrors({});
      setIsEditMode(false);
      setEditingIndex(null);

      // Close dialog
      dialogRef?.current?.hide();

      // Refresh data
      await getActorsData();
    } catch (err) {
      console.error(err);
      setOpenAlertOptions(["toast", "error"]);
      setAlertMessage(
        `Failed to ${isEditMode ? "update" : "add"} actor: ` + err.message
      );
      setTimeout(() => {
        setOpenAlertOptions(undefined);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="dialog-header">
      <div
        style={{
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <SafText appearance="heading-2xl">Actors</SafText>
      </div>
      <br />

      <div style={{ maxWidth: "1000px", margin: "0" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <SafText appearance="body-small-md">
            Please add all the actors that use or have a dependency on this
            assets service.
          </SafText>
          <SafButton
            size="small"
            ref={buttonRef}
            onClick={handleAddClick}
            id="button-dialog-trigger"
            aria-haspopup="dialog"
            aria-controls="button-dialog-overlay"
            aria-expanded={isOpen ? "true" : "false"}
            type="button"
          >
            <SafIcon
              slot="start"
              icon-name="plus"
              appearance="solid"
              presentation
            ></SafIcon>
            Add
          </SafButton>
        </div>
        <br />
        <SafTable
          alternating-rows="true"
          density="inherit"
          header-background="default"
          inline-borders="true"
          width="100%"
        >
          <table>
            <thead>
              <tr>
                <th style={{ width: "300px" }}>Actor Name</th>
                <th style={{ width: "300px" }}>Location</th>
                <th style={{ width: "300px" }}>Description</th>
                <th style={{ width: "300px" }}>Edit</th>
                <th style={{ width: "300px" }}>Delete</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    Loading...
                  </td>
                </tr>
              ) : actorData &&
                actorData.actors &&
                actorData.actors.length > 0 ? (
                actorData.actors.map((actor, idx) => (
                  <tr key={idx}>
                    <td>{actor.actor_name}</td>
                    <td>{actor.location}</td>
                    <td>{actor.description}</td>
                    <td>
                      <SafIcon
                        icon-name="pencil"
                        appearance="solid"
                        color="#404040"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleEditClick(actor, idx)}
                      />
                    </td>
                    <td>
                      <SafIcon
                        icon-name="trash-can"
                        appearance="solid"
                        color="#404040"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDeleteClick(actor, idx)} // Added click handler
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SafTable>
      </div>
      <div>
        <div>
          {openAlertOptions && (
            <SafAlert
              appearance={openAlertOptions[1]}
              alertType={openAlertOptions[0]}
              iconAriaLabel="icon aria label"
              closeAriaLabel="Close"
              onClose={handleOnClose}
            >
              {alertMessage}
            </SafAlert>
          )}

          {/* Add/Edit Dialog */}
          <SafDialog
            id="button-dialog-overlay"
            modal={false}
            isAlert="false"
            isHeader="true"
            isFooter="true"
            noFocusTrap={false}
            dialogTitle={isEditMode ? "Edit Actor" : "Add Actor"}
            ref={dialogRef}
            hidden={!isOpen}
            triggerId="button-dialog-trigger"
            ZIndex="-1"
          >
            <div>
              <SafTextComp
                label="Actor Name"
                name="actor_name"
                value={formData.actor_name}
                validationMessage={errors.actor_name}
                onChange={handleInputChange}
                onBlur={validateForm}
              />
              {errors.actor_name && (
                <span style={{ color: "red" }}>{errors.actor_name}</span>
              )}
              <SafTextComp
                label="Location"
                name="location"
                value={formData.location}
                validationMessage={errors.location}
                onChange={handleInputChange}
                onBlur={validateForm}
              />
              {errors.location && (
                <span style={{ color: "red" }}>{errors.location}</span>
              )}

              <SafTextComp
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                validationMessage={errors.description}
                onBlur={validateForm}
              />
              {errors.description && (
                <span style={{ color: "red" }}>{errors.description}</span>
              )}
            </div>

            <div
              slot="footer"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <SafButton
                appearance="secondary"
                onClick={handleCancelClick}
                type="button"
              >
                Cancel
              </SafButton>
              <SafButton
                appearance="primary"
                onClick={handleSubmitForm}
                type="button"
              >
                {isEditMode ? "Update" : "Save"}
              </SafButton>
            </div>
          </SafDialog>

          {/* Delete Confirmation Dialog */}
          <SafDialog
            id="delete-dialog-overlay"
            modal={true}
            isAlert="true"
            isHeader="true"
            isFooter="true"
            noFocusTrap={false}
            dialogTitle="Delete Actor"
            ref={deleteDialogRef}
            hidden={!isDeleteDialogOpen}
            ZIndex="1"
          >
            <div style={{ padding: "20px 0" }}>
              <SafText appearance="body-md">
                Are you sure you want to delete this actor?
              </SafText>
              <br />
            </div>

            <div
              slot="footer"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <SafButton
                appearance="secondary"
                onClick={handleDeleteCancelClick}
                type="button"
              >
                No
              </SafButton>
              <SafButton
                appearance="danger"
                onClick={handleDeleteConfirm}
                type="button"
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </SafButton>
            </div>
          </SafDialog>
        </div>
      </div>

      <br />
      <br />
    </div>
  );
};

export default Actors;
